import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CForm,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CRow,
  CSpinner,
} from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";
import { useJobPolling, useOptimizations, useStrategies } from "@src/hooks/redux";
import { useCommonSettingsUrlParams } from "@src/hooks/useCommonSettingsUrlParams";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type { AppOptimizationStudyResult } from "@src/types";
import { formatToISO8601WithTimezone } from "@src/utils/dateUtils";
import { hydrateApiStudy, sortObjectKeys } from "@src/utils/formatters";
import type * as TDT from "@tradingdroid/frontends-types";
import type { ParamOverride } from "@tradingdroid/frontends-types";
import dayjs from "dayjs";

import { CJsonTextArea } from "../components/CJsonTextArea";
import {
  ParamOverridesEditor,
  buildFullOverridesPayload,
  describeEmptyConditionalBranch,
  findEmptyConditionalBranches,
} from "@src/components/ParamOverridesEditor";
import { CommonSettingsBlock } from "../components/CommonSettingsBlock";
import { ResultsStudyBlock } from "../components/ResultsStudyBlock";
import { buildDefaultConfig } from "@src/utils/configBuilder";
import { httpStatusOf } from "@src/utils/apiError";

const defaultFormData = {
  // "study" = a plain single-window optimization; "walk_forward" = the same inner
  // optimization re-run on rolling windows. Both launch from this one form.
  mode: "study" as "study" | "walk_forward",
  strategyId: 0,
  exchangeName: "",
  instrument: "",
  timeframe: "5m",
  startDateTime: dayjs().subtract(1, "year").format("YYYY-MM-DDTHH:mm"),
  endDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
  investmentAmount: 1000,
  cumulativeInvestment: true,
  optimizeSpaces: {
    entry: true,
    exit: true,
  },
  numberOfTrials: 50,
  numberOfJobs: 6,
  objective: "cumulative_returns",
  studyName: `Study ${dayjs().format("YYYY-MM-DD_HH:mm:ss")}`,
  strategyParamOverrides: {} as Record<string, ParamOverride>,
  exitParamOverrides: {} as Record<string, ParamOverride>,
  regimeParamOverrides: {} as Record<string, ParamOverride>,
  // Walk-forward-only fields (ignored when mode === "study").
  isDuration: "90d",
  oosDuration: "30d",
  stepDuration: "30d",
  minFolds: 3,
  anchored: false,
  selectionRule: "best_objective" as "best_objective" | "best_plateau",
};

export const OptimizeExecPage: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Get optimization ID from URL if viewing existing
  const { showToast } = useToastActions();
  const { showJsonModal } = useJsonModal();
  const { trackActiveJob, activeJobs } = useJobPolling();
  const { optimizations } = useOptimizations();
  const { getStrategyById } = useStrategies();

  const [running, setRunning] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [selectedStudyId, setSelectedStudyId] = useState<number | null>(
    id ? Number(id) : null, // Initialize from URL param if present
  );
  const [formExpanded, setFormExpanded] = useState(!id); // Collapse if viewing existing
  const [submittedJobId, setSubmittedJobId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [study, setStudy] = useState<TDT.ApiStudy | null>(null);
  const [transformedStudy, setTransformedStudy] = useState<AppOptimizationStudyResult | null>(null);

  // Load common settings from URL params (for dev reload persistence)
  const handleUrlParamsLoad = useCallback((settings: Partial<typeof defaultFormData>) => {
    setFormData((prev) => ({ ...prev, ...settings }));
  }, []);
  useCommonSettingsUrlParams({
    skip: !!id, // Skip if viewing existing study
    onLoad: handleUrlParamsLoad,
  });

  // Load study ID from URL param on mount
  useEffect(() => {
    if (id) {
      const studyId = Number(id);
      setSelectedStudyId(studyId);
      setFormExpanded(false); // Start collapsed when viewing existing
    }
  }, [id]);

  // Fetch full study details with trials and job data
  useEffect(() => {
    const fetchFullDetails = async () => {
      if (!selectedStudyId) return;

      // Check if we already have full data with trials
      if (study?.trials && study.trials.length > 0) return;

      setLoading(true);
      setError(null);

      try {
        const fullStudy = await tdApi.getStudy(selectedStudyId, { embed: "job" });
        setStudy(fullStudy || null);

        // Transform and set the hydrated data
        if (fullStudy?.trials && fullStudy.trials.length > 0) {
          const hydratedStudy = hydrateApiStudy(fullStudy);
          setTransformedStudy(hydratedStudy);
        }
      } catch (error) {
        // A 404 means the optimization doesn't exist or the caller doesn't own it (studies are now
        // owner-scoped) — show a plain not-found message, not a scary error toast.
        if (httpStatusOf(error) === 404) {
          setStudy(null);
          setError(
            "Optimization not found — it may have been removed, or you don't have access to it.",
          );
        } else {
          const message = error instanceof Error ? error.message : String(error);
          setError(message);
          showToast({
            message: `Failed to load optimization details: ${message}`,
            type: "danger",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFullDetails();
  }, [selectedStudyId, study?.trials, showToast]);

  // Populate form with job metadata when study is loaded
  // Use ref to track which job we've populated to prevent infinite loops
  const populatedJobIdRef = React.useRef<number | null>(null);

  useEffect(() => {
    const jobId = study?.job?.id;
    if (!jobId || populatedJobIdRef.current === jobId) return;

    const job = study.job!;
    const strategyId = job.strategy_id;

    console.log(
      `[OptimizeExecPage] Populating form from job ${job.id} (strategy: ${strategyId}, ${job.exchange_name} ${job.symbol_name})`,
    );

    setFormData((prev) => ({
      ...prev,
      strategyId: strategyId || 0,
      exchangeName: job.exchange_name || "",
      instrument: job.symbol_name || "",
      timeframe: job.period || "",
      startDateTime: job.from_ts ? dayjs(job.from_ts).format("YYYY-MM-DDTHH:mm") : "",
      endDateTime: job.end_ts ? dayjs(job.end_ts).format("YYYY-MM-DDTHH:mm") : "",
      investmentAmount: job.config.portfolio.initial_capital,
      studyName: study.study_name || "",
    }));

    populatedJobIdRef.current = jobId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.job?.id, study?.study_name]);

  // Watch for job completion - if our submitted job completes, show results and collapse form
  useEffect(() => {
    if (submittedJobId) {
      const completedOptimization = optimizations.find((opt) => opt.study_id === submittedJobId);
      if (completedOptimization && !selectedStudyId) {
        // Job just completed! Show results and collapse form
        setSelectedStudyId(submittedJobId);
        setFormExpanded(false);
        setSubmittedJobId(null); // Reset so we don't trigger again
      }
    }
  }, [optimizations, submittedJobId, selectedStudyId]);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    // Reset overrides when strategy changes (params may differ between strategies)
    if (field === "strategyId") {
      setFormData((prev) => ({
        ...prev,
        strategyId: value as number,
        strategyParamOverrides: {},
        exitParamOverrides: {},
        regimeParamOverrides: {},
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSpaceChange = (space: "entry" | "exit", checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      optimizeSpaces: {
        ...prev.optimizeSpaces,
        [space]: checked,
      },
    }));
  };

  const validateForm = () => {
    if (!formData.strategyId) return "Strategy is required";
    if (!formData.exchangeName) return "Exchange is required";
    if (!formData.instrument.trim()) return "Instrument is required";
    if (!formData.optimizeSpaces.entry && !formData.optimizeSpaces.exit) {
      return "At least one optimize space must be selected";
    }
    if (formData.mode === "walk_forward") {
      if (!formData.isDuration.trim()) return "In-sample duration is required";
      if (!formData.oosDuration.trim()) return "Out-of-sample duration is required";
      if (!formData.stepDuration.trim()) return "Advance (step) duration is required";
      if (!Number.isInteger(formData.minFolds) || formData.minFolds < 1) {
        return "Min folds must be a positive integer";
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showToast({ message: validationError, type: "danger" });
      return;
    }

    setRunning(true);

    try {
      // Create optimization job via API
      const symbolName = formData.instrument;

      // Build full overrides payload from displayed values (WYSIWYG)
      // This ensures the backend receives exactly what the user sees in the UI
      const selectedStrategy = getStrategyById(formData.strategyId);

      const strategySchema = selectedStrategy?.parameters_schema?.strategy_config || {};
      const exitSchema = selectedStrategy?.parameters_schema?.exit_config || {};
      const regimeSchema = selectedStrategy?.parameters_schema?.regime_config || {};

      // Build strategy defaults from parameters_schema
      const defaultConfig = selectedStrategy ? buildDefaultConfig(selectedStrategy) : {};
      const strategyDefaults =
        (defaultConfig as Record<string, Record<string, unknown>>).strategy_config || {};
      const exitDefaults =
        (defaultConfig as Record<string, Record<string, unknown>>).exit_config || {};

      // Build full payload - send ALL displayed values, not just "dirty" ones
      const fullStrategyOverrides = buildFullOverridesPayload(
        strategySchema,
        strategyDefaults,
        formData.strategyParamOverrides,
      );
      const fullExitOverrides = buildFullOverridesPayload(
        exitSchema,
        exitDefaults,
        formData.exitParamOverrides,
      );
      // Regime defaults stay out on purpose: a default regime_enabled=false would pin the filter
      // off, while the schema alone lets the study search the switch and every detector.
      const fullRegimeOverrides = buildFullOverridesPayload(
        regimeSchema,
        {},
        formData.regimeParamOverrides,
      );

      // A worker that honours the selection refuses these at start, after a claim, and an older
      // one searches values the user did not keep. Refuse them here instead, with the study the
      // user has open left in place.
      const emptyBranches = [
        ...findEmptyConditionalBranches(strategySchema, fullStrategyOverrides),
        ...findEmptyConditionalBranches(exitSchema, fullExitOverrides),
        ...findEmptyConditionalBranches(regimeSchema, fullRegimeOverrides),
      ];
      if (emptyBranches.length > 0) {
        showToast({
          title: "Optimization not started",
          message: emptyBranches.map(describeEmptyConditionalBranch).join(" "),
          type: "danger",
        });
        return;
      }

      setSelectedStudyId(null);

      // The inner optimization block is identical for both modes (WYSIWYG: send
      // ALL displayed values). Walk-forward wraps it with windowing; a plain
      // study posts it directly.
      const optimizationConfig = {
        n_trials: formData.numberOfTrials,
        objective: formData.objective,
        timeout: 3600,
        // Portfolio config (v0.7.30+)
        portfolio: {
          initial_capital: formData.investmentAmount,
          cumulative_investment: formData.cumulativeInvestment,
        },
        cost_config: {
          apply_fees: true,
          apply_slippage: true,
        },
        strategy_param_overrides:
          Object.keys(fullStrategyOverrides).length > 0 ? fullStrategyOverrides : undefined,
        exit_param_overrides:
          Object.keys(fullExitOverrides).length > 0 ? fullExitOverrides : undefined,
        regime_param_overrides:
          Object.keys(fullRegimeOverrides).length > 0 ? fullRegimeOverrides : undefined,
      };

      const commonFields = {
        strategy_id: formData.strategyId,
        exchange_name: formData.exchangeName,
        symbol_name: symbolName,
        period: formData.timeframe,
        from_ts: formatToISO8601WithTimezone(formData.startDateTime),
        end_ts: formatToISO8601WithTimezone(formData.endDateTime),
        description: formData.studyName,
      };

      const isWalkForward = formData.mode === "walk_forward";
      const jobData: TDT.ApiJobCreate = isWalkForward
        ? {
            job_type_name: "walk_forward",
            ...commonFields,
            config: {
              // Inner optimization runs single-process: fold-level parallelism
              // already spreads the work; joblib on top would multiply processes.
              optimization: { ...optimizationConfig, n_jobs: 1 },
              windowing: {
                is_duration: formData.isDuration,
                oos_duration: formData.oosDuration,
                step_duration: formData.stepDuration,
                min_folds: formData.minFolds,
                anchored: formData.anchored,
                // Form timestamps land on the current minute, which is not a candle
                // boundary at most timeframes; snap floors from_ts so the default
                // flow doesn't 422 under the backend's strict alignment.
                boundary_alignment: "snap",
              },
              selection_rule: formData.selectionRule,
              max_parallel_folds: 1,
            },
          }
        : {
            job_type_name: "optimization",
            ...commonFields,
            config: {
              ...optimizationConfig,
              n_jobs: formData.numberOfJobs === 0 ? undefined : formData.numberOfJobs, // 0 = auto
            },
          };

      const job = await tdApi.createJob(jobData);

      if (!job) {
        throw new Error("Failed to create job");
      }

      // Track the job for polling
      trackActiveJob({
        jobId: job.id,
        jobType: isWalkForward ? "walk_forward" : "optimization",
        ...(isWalkForward ? {} : { entityType: "optimization" as const }),
        metadata: {
          studyName: formData.studyName,
          strategyId: formData.strategyId,
        },
      });

      // Store job ID so we can detect when it completes
      setSubmittedJobId(job.id);

      showToast({
        message: `${isWalkForward ? "Walk-forward" : "Optimization"} job #${job.id} created for ${symbolName} on ${formData.exchangeName}`,
        type: "success",
      });
    } catch (err) {
      console.error("Failed to create optimization job:", err);
      showToast({ message: "Failed to start optimization. Please try again.", type: "danger" });
    } finally {
      setRunning(false);
    }
  };

  const isWalkForwardMode = formData.mode === "walk_forward";
  const isJobRunning = activeJobs.some(
    (job) =>
      (job.jobType === "optimization" || job.jobType === "walk_forward") &&
      job.jobId === submittedJobId,
  );

  const renderStrategyConfig = () => {
    const selectedStrategy = formData.strategyId > 0 ? getStrategyById(formData.strategyId) : null;

    const defaultConfig = selectedStrategy ? buildDefaultConfig(selectedStrategy) : {};

    // Build config object with sorted keys
    const configValue: Record<string, unknown> = {};
    const typedConfig = defaultConfig as Record<string, Record<string, unknown>>;
    if (typedConfig.strategy_config && Object.keys(typedConfig.strategy_config).length > 0) {
      configValue.strategy_config = sortObjectKeys(typedConfig.strategy_config);
    }
    if (typedConfig.exit_config && Object.keys(typedConfig.exit_config).length > 0) {
      configValue.exit_config = sortObjectKeys(typedConfig.exit_config);
    }

    const jsonValue =
      Object.keys(configValue).length > 0 ? JSON.stringify(configValue, null, 2) : "";

    return (
      <CCard className="mb-2">
        <CCardHeader className="py-1 px-2 d-flex align-items-center justify-content-between">
          <h6 className="mb-0">Strategy Default Config</h6>
          <small className="text-body-secondary">readonly</small>
        </CCardHeader>
        <CCardBody className="p-2">
          <CJsonTextArea
            value={jsonValue}
            readOnly
            rows={14}
            placeholder="Select a strategy to view its default configuration..."
          />
        </CCardBody>
      </CCard>
    );
  };

  const renderParamOverrides = () => {
    const selectedStrategy = formData.strategyId > 0 ? getStrategyById(formData.strategyId) : null;

    // Show hint when no strategy selected
    if (!selectedStrategy) {
      return (
        <CCard className="mt-2 mb-2">
          <CCardHeader className="py-1 px-2">
            <h6 className="mb-0">Parameter Overrides</h6>
          </CCardHeader>
          <CCardBody className="p-2">
            <div className="text-body-secondary small text-center py-3">
              Select a strategy above to customize optimization parameters.
              <br />
              <span className="text-muted">
                You can lock specific values, narrow search ranges, or limit category options.
              </span>
            </div>
          </CCardBody>
        </CCard>
      );
    }

    // Get schema from strategy (has type info, categories, ranges)
    const strategySchema = selectedStrategy?.parameters_schema?.strategy_config || {};
    const exitSchema = selectedStrategy?.parameters_schema?.exit_config || {};
    const regimeSchema = selectedStrategy?.parameters_schema?.regime_config || {};

    // Get strategy default config values as defaults/hints
    const defaultConfig = buildDefaultConfig(selectedStrategy);
    const typedConfig = defaultConfig as Record<string, Record<string, unknown>>;
    const strategyDefaults = typedConfig.strategy_config || {};
    const exitDefaults = typedConfig.exit_config || {};

    // Show if we have either schema params OR default params
    const hasStrategyParams =
      Object.keys(strategySchema).length > 0 || Object.keys(strategyDefaults).length > 0;
    const hasExitParams =
      Object.keys(exitSchema).length > 0 || Object.keys(exitDefaults).length > 0;
    const hasRegimeParams = Object.keys(regimeSchema).length > 0;

    if (!hasStrategyParams && !hasExitParams && !hasRegimeParams) {
      return null;
    }
    const columnWidth = hasRegimeParams ? 4 : 6;

    return (
      <CCard className="mt-2 mb-2">
        <CCardHeader className="py-1 px-2">
          <h6 className="mb-0">Parameter Overrides</h6>
        </CCardHeader>
        <CCardBody className="p-2">
          <CRow>
            {hasStrategyParams && (
              <CCol md={columnWidth}>
                <h6 className="small text-body-secondary mb-2">Strategy Parameters</h6>
                <ParamOverridesEditor
                  parameterSchema={strategySchema}
                  strategyDefaults={strategyDefaults}
                  overrides={formData.strategyParamOverrides}
                  onChange={(overrides) =>
                    setFormData((prev) => ({ ...prev, strategyParamOverrides: overrides }))
                  }
                  disabled={running}
                />
              </CCol>
            )}
            {hasExitParams && (
              <CCol md={columnWidth}>
                <h6 className="small text-body-secondary mb-2">Exit Parameters</h6>
                <ParamOverridesEditor
                  parameterSchema={exitSchema}
                  strategyDefaults={exitDefaults}
                  overrides={formData.exitParamOverrides}
                  onChange={(overrides) =>
                    setFormData((prev) => ({ ...prev, exitParamOverrides: overrides }))
                  }
                  disabled={running}
                />
              </CCol>
            )}
            {hasRegimeParams && (
              <CCol md={columnWidth}>
                <h6 className="small text-body-secondary mb-2">Regime Filter</h6>
                <ParamOverridesEditor
                  parameterSchema={regimeSchema}
                  overrides={formData.regimeParamOverrides}
                  onChange={(overrides) =>
                    setFormData((prev) => ({ ...prev, regimeParamOverrides: overrides }))
                  }
                  disabled={running}
                />
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>
    );
  };

  return (
    <div className="OptimizeExecPage">
      {/* Form Section - Collapsible */}
      <CCard className="mb-3">
        <CCardHeader className="d-flex align-items-center gap-2">
          <h5 className="mb-0">Optimization Setup</h5>
          {transformedStudy && (
            <CButton
              color="link"
              size="sm"
              className="p-0 me-2"
              onClick={() => {
                const {
                  all_trials: _all_trials,
                  best_trials: _best_trials,
                  ...optResultsWithoutTrials
                } = transformedStudy;
                showJsonModal(
                  optResultsWithoutTrials,
                  `Optimization Summary - ${transformedStudy.study_name}`,
                );
              }}
              title="View optimization data"
            >
              ℹ️
            </CButton>
          )}
          <CButton
            color="link"
            size="sm"
            className="p-0"
            onClick={() => setFormExpanded(!formExpanded)}
            title={formExpanded ? "Collapse" : "Expand"}
          >
            {formExpanded ? "🔽" : "▶️"}
          </CButton>
        </CCardHeader>
        <CCollapse visible={formExpanded}>
          <CCardBody>
            {!id && (
              <div className="mb-3">
                <CButtonGroup role="group" aria-label="Optimization type">
                  <CButton
                    color={isWalkForwardMode ? "secondary" : "primary"}
                    variant={isWalkForwardMode ? "outline" : undefined}
                    onClick={() => handleInputChange("mode", "study")}
                    disabled={running}
                  >
                    Study
                  </CButton>
                  <CButton
                    color={isWalkForwardMode ? "primary" : "secondary"}
                    variant={isWalkForwardMode ? undefined : "outline"}
                    onClick={() => handleInputChange("mode", "walk_forward")}
                    disabled={running}
                  >
                    Walk-Forward
                  </CButton>
                </CButtonGroup>
                <div className="small text-body-secondary mt-1">
                  {isWalkForwardMode
                    ? "Re-optimizes on rolling windows and validates each on unseen data (out-of-sample)."
                    : "A single optimization over the whole date range."}
                </div>
              </div>
            )}
            <CRow className="g-2">
              <CCol lg={6}>
                <CCard>
                  <CCardHeader className="py-1 px-2">
                    <h6 className="mb-0">Settings</h6>
                  </CCardHeader>
                  <CCardBody className="p-2">
                    <CForm onSubmit={handleSubmit}>
                      <CommonSettingsBlock
                        strategyId={formData.strategyId}
                        exchangeName={formData.exchangeName}
                        instrument={formData.instrument}
                        timeframe={formData.timeframe}
                        startDateTime={formData.startDateTime}
                        endDateTime={formData.endDateTime}
                        investmentAmount={formData.investmentAmount}
                        cumulativeInvestment={formData.cumulativeInvestment}
                        onChange={handleInputChange}
                        disabled={running}
                      />
                    </CForm>
                  </CCardBody>
                </CCard>
              </CCol>

              <CCol lg={6}>
                {/* Strategy Default Configs - Readonly display of selected strategy's default params */}
                {renderStrategyConfig()}

                <CCard>
                  <CCardHeader className="py-1 px-2">
                    <h6 className="mb-0">Config Settings</h6>
                  </CCardHeader>
                  <CCardBody className="p-2">
                    <CRow className="mb-3">
                      <CCol md={4}>
                        <CFormLabel>Optimize Space</CFormLabel>
                        <div>
                          <CFormCheck
                            id="entry-space"
                            label="Entry"
                            checked={formData.optimizeSpaces.entry}
                            onChange={(e) => handleSpaceChange("entry", e.target.checked)}
                            disabled={running}
                          />
                          <CFormCheck
                            id="exit-space"
                            label="Exit"
                            checked={formData.optimizeSpaces.exit}
                            onChange={(e) => handleSpaceChange("exit", e.target.checked)}
                            disabled={running}
                          />
                        </div>
                      </CCol>
                      <CCol md={4}>
                        <CFormLabel>Number of Trials</CFormLabel>
                        <CFormInput
                          type="number"
                          value={formData.numberOfTrials}
                          onChange={(e) =>
                            handleInputChange("numberOfTrials", Number(e.target.value))
                          }
                          min={1}
                          disabled={running}
                        />
                      </CCol>
                      {!isWalkForwardMode && (
                        <CCol md={4}>
                          <CFormLabel>
                            CPU Cores <small className="text-body-secondary">(0 = auto)</small>
                          </CFormLabel>
                          <CFormInput
                            type="number"
                            value={formData.numberOfJobs ?? 0}
                            onChange={(e) =>
                              handleInputChange("numberOfJobs", Number(e.target.value))
                            }
                            disabled={running}
                          />
                        </CCol>
                      )}
                    </CRow>

                    <div className="mb-3">
                      <CFormLabel>Optimization Objective</CFormLabel>
                      <CFormSelect
                        value={formData.objective}
                        onChange={(e) => handleInputChange("objective", e.target.value)}
                        disabled={running}
                      >
                        <option value="cumulative_returns">Cumulative Returns (Currency)</option>
                        <option value="cumulative_returns_pct">Cumulative Returns (%)</option>
                        <option value="win_rate">Win Rate</option>
                        <option value="profit_factor">Profit Factor</option>
                        <option value="sharpe_ratio">Sharpe Ratio</option>
                        <option value="sortino_ratio">Sortino Ratio</option>
                        <option value="max_drawdown">Max Drawdown (Currency)</option>
                        <option value="max_drawdown_pct">Max Drawdown (%)</option>
                        <option value="total_positions">Total Positions</option>
                        <option value="win_signals">Win Signals</option>
                        <option value="loss_signals">Loss Signals</option>
                        <option value="skewness">Skewness</option>
                        <option value="kurtosis">Kurtosis</option>
                      </CFormSelect>
                    </div>

                    <div className="mb-3">
                      <CFormLabel>Study Name</CFormLabel>
                      <CFormInput
                        type="text"
                        value={formData.studyName}
                        onChange={(e) => handleInputChange("studyName", e.target.value)}
                        disabled={running}
                      />
                    </div>
                  </CCardBody>
                </CCard>

                {isWalkForwardMode && (
                  <CCard className="mt-2">
                    <CCardHeader className="py-1 px-2">
                      <h6 className="mb-0">Walk-Forward Windows</h6>
                    </CCardHeader>
                    <CCardBody className="p-2">
                      <CRow className="mb-3">
                        <CCol md={4}>
                          <CFormLabel>
                            Training <small className="text-body-secondary">(in-sample)</small>
                          </CFormLabel>
                          <CFormInput
                            value={formData.isDuration}
                            onChange={(e) => handleInputChange("isDuration", e.target.value)}
                            placeholder="90d"
                            disabled={running}
                          />
                        </CCol>
                        <CCol md={4}>
                          <CFormLabel>
                            Test <small className="text-body-secondary">(out-of-sample)</small>
                          </CFormLabel>
                          <CFormInput
                            value={formData.oosDuration}
                            onChange={(e) => handleInputChange("oosDuration", e.target.value)}
                            placeholder="30d"
                            disabled={running}
                          />
                        </CCol>
                        <CCol md={4}>
                          <CFormLabel>
                            Advance <small className="text-body-secondary">(step)</small>
                          </CFormLabel>
                          <CFormInput
                            value={formData.stepDuration}
                            onChange={(e) => handleInputChange("stepDuration", e.target.value)}
                            placeholder="30d"
                            disabled={running}
                          />
                        </CCol>
                      </CRow>
                      <CRow className="align-items-end">
                        <CCol md={4}>
                          <CFormLabel>Min folds</CFormLabel>
                          <CFormInput
                            type="number"
                            min={1}
                            value={formData.minFolds}
                            onChange={(e) => handleInputChange("minFolds", Number(e.target.value))}
                            disabled={running}
                          />
                        </CCol>
                        <CCol md={8}>
                          <CFormLabel>Fold winner</CFormLabel>
                          <CFormSelect
                            value={formData.selectionRule}
                            onChange={(e) => handleInputChange("selectionRule", e.target.value)}
                            disabled={running}
                          >
                            <option value="best_objective">
                              Best objective (top-scoring trial)
                            </option>
                            <option value="best_plateau">
                              Best plateau (flattest neighborhood)
                            </option>
                          </CFormSelect>
                        </CCol>
                      </CRow>
                      <div className="mt-3">
                        <CFormCheck
                          id="wf-anchored"
                          label="Anchored history (grow the training window instead of sliding it)"
                          checked={formData.anchored}
                          onChange={(e) => handleInputChange("anchored", e.target.checked)}
                          disabled={running}
                        />
                      </div>
                    </CCardBody>
                  </CCard>
                )}
              </CCol>
            </CRow>

            {/* Parameter Overrides - full width */}
            {renderParamOverrides()}

            {/* Submit Button */}
            <CRow className="mt-3">
              <CCol>
                <div className="d-grid">
                  <CButton
                    type="button"
                    color="primary"
                    size="lg"
                    disabled={running || isJobRunning}
                    onClick={handleSubmit}
                  >
                    {running || isJobRunning ? (
                      <CSpinnerText
                        text={
                          isWalkForwardMode
                            ? isJobRunning
                              ? "Walk-Forward Running..."
                              : "Starting Walk-Forward..."
                            : isJobRunning
                              ? "Optimization Running..."
                              : "Starting Optimization..."
                        }
                        gap="2"
                      />
                    ) : isWalkForwardMode ? (
                      "Run Walk-Forward"
                    ) : (
                      "Run Optimization"
                    )}
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCollapse>
      </CCard>

      {/* Loading State - Study data being fetched */}
      {loading && (
        <CCard>
          <CCardBody className="text-center p-4">
            <CSpinner />
            <p className="mt-2">Loading study details...</p>
          </CCardBody>
        </CCard>
      )}

      {/* Error State - Failed to load study */}
      {error && !loading && (
        <CCard>
          <CCardBody className="text-center p-4">
            <div className="text-danger mb-3">
              <strong>Error Loading Study</strong>
            </div>
            <p>{error}</p>
            <CButton
              color="primary"
              size="sm"
              onClick={() => {
                setError(null);
                setStudy(null);
              }}
            >
              Retry
            </CButton>
          </CCardBody>
        </CCard>
      )}

      {/* Results Section - Only shows when study is selected and loaded */}
      {transformedStudy && study && !loading && !error && (
        <ResultsStudyBlock
          optResults={transformedStudy}
          optSettings={{
            strategyId: formData.strategyId || study.job?.strategy_id || 0,
            exchangeName: study.job?.exchange_name || "",
            instrument: study.job?.symbol_name || "",
            timeframe: study.job?.period || "5m",
            startDateTime: study.job?.from_ts
              ? dayjs(study.job.from_ts).format("YYYY-MM-DDTHH:mm")
              : formData.startDateTime,
            endDateTime: study.job?.end_ts
              ? dayjs(study.job.end_ts).format("YYYY-MM-DDTHH:mm")
              : formData.endDateTime,
            investmentAmount: study.job!.config.portfolio.initial_capital,
            cumulativeInvestment: true,
          }}
          optimizationJobId={study.job!.id}
        />
      )}

      {/* Show message if job is running */}
      {isJobRunning && !study && (
        <CCard>
          <CCardBody className="text-center p-4">
            <CSpinner />
            <p className="mt-3 mb-0">
              {isWalkForwardMode
                ? "Walk-forward running. Track it and view its results from the Jobs list."
                : "Optimization running... Results will appear here when complete."}
            </p>
          </CCardBody>
        </CCard>
      )}
    </div>
  );
};
